/**
 * Editing a decoration's configuration.
 *
 * A colour decoration stores everything about itself in one JSON column, so
 * there is nothing here for the server to change a piece of: every edit is a
 * whole new `value_provider_conf` written over the old one. These are the pure
 * functions that build it, kept apart from the mutation so they can be tested
 * against plain objects.
 *
 * Every one returns a new configuration and leaves the one it was given alone,
 * which is what lets the caller compare the two and write back only a real
 * change.
 *
 * Ids are handed in rather than made here. A colour, a condition and a group
 * are each named by a string somebody invents -- nothing issues them -- and a
 * function that invented its own would be a function whose answer is different
 * every time it is called.
 */
import type {
  ConditionalColor,
  ConditionalColorConf,
  ConditionalColorFilter,
  ConditionalColorFilterGroup,
  Field,
  SelectColorConf,
} from '@/api/types'
import { groupsWithin } from '../rows/rowMatch'
import { SELECT_COLOR_NAMES } from '../fields/select-options'

/**
 * What a new `single_select_color` decoration starts as: the first select
 * column in the table, or nothing when the table has none.
 *
 * A decoration naming no column colours nothing, which is a fair state to
 * create -- the column is the one thing this provider is configured by, so
 * offering it already pointed at the only plausible answer saves the common
 * case a step and says what the provider is for in the uncommon one.
 */
export function defaultSelectConf(fields: readonly Field[]): SelectColorConf {
  const select = fields.find((field) => field.type === 'single_select')
  return { field_id: select?.id ?? null }
}

/**
 * What a new `conditional_color` decoration starts as: one colour with no
 * conditions.
 *
 * A colour with no conditions matches every row -- the backend says so -- so a
 * new decoration paints the whole table at once. That is deliberate: the rule
 * somebody just added is visible immediately and is then narrowed, rather than
 * being stored as an empty list that does nothing and looks broken.
 */
export function defaultConditionalConf(
  colorId: string,
  color: string,
): ConditionalColorConf {
  return { colors: [{ id: colorId, color, filters: [], operator: 'AND' }] }
}

/** The next colour to offer, in palette order, skipping the ones in use. */
export function nextColor(conf: ConditionalColorConf): string {
  const taken = new Set(conf.colors.map((color) => color.color))
  return SELECT_COLOR_NAMES.find((name) => !taken.has(name)) ?? SELECT_COLOR_NAMES[0]!
}

/** Replaces one colour in the list, by id. */
export function patchColor(
  conf: ConditionalColorConf,
  colorId: string,
  change: (color: ConditionalColor) => ConditionalColor,
): ConditionalColorConf {
  return {
    ...conf,
    colors: conf.colors.map((color) => (color.id === colorId ? change(color) : color)),
  }
}

export function withColor(
  conf: ConditionalColorConf,
  color: ConditionalColor,
): ConditionalColorConf {
  return { ...conf, colors: [...conf.colors, color] }
}

export function withoutColor(
  conf: ConditionalColorConf,
  colorId: string,
): ConditionalColorConf {
  return { ...conf, colors: conf.colors.filter((color) => color.id !== colorId) }
}

/**
 * Adds a condition to a colour, or replaces the one it already holds with that
 * id.
 *
 * One function for adding and for changing, the same shape a view's filters
 * take, because the two answers differ only in whether the id is already there.
 */
export function withCondition(
  color: ConditionalColor,
  condition: ConditionalColorFilter,
): ConditionalColor {
  const held = color.filters.some((candidate) => candidate.id === condition.id)
  return {
    ...color,
    filters: held
      ? color.filters.map((candidate) =>
          candidate.id === condition.id ? condition : candidate,
        )
      : [...color.filters, condition],
  }
}

export function withoutCondition(
  color: ConditionalColor,
  conditionId: string,
): ConditionalColor {
  return {
    ...color,
    filters: color.filters.filter((condition) => condition.id !== conditionId),
  }
}

export function withGroup(
  color: ConditionalColor,
  group: ConditionalColorFilterGroup,
): ConditionalColor {
  const groups = color.filter_groups ?? []
  const held = groups.some((candidate) => candidate.id === group.id)
  return {
    ...color,
    filter_groups: held
      ? groups.map((candidate) => (candidate.id === group.id ? group : candidate))
      : [...groups, group],
  }
}

/**
 * Removes a group, everything nested inside it, and every condition in any of
 * them -- the same rule a view's filter groups follow, through the same walk.
 */
export function withoutGroup(color: ConditionalColor, groupId: string): ConditionalColor {
  const groups = color.filter_groups ?? []
  const removed = groupsWithin(groups, groupId)

  return {
    ...color,
    filter_groups: groups.filter((group) => !removed.has(group.id)),
    filters: color.filters.filter(
      (condition) => condition.group == null || !removed.has(condition.group),
    ),
  }
}
